import { useEffect, useRef, useState } from "react";
import { Check, ChevronsUpDown, Loader2, Search, Car, User } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { CustomerAPI, VehicleAPI } from "@/lib/api";
import { cn } from "@/lib/utils";

/**
 * Combined searchable customer + vehicle picker. Emits `{ customer, vehicle }`
 * via `onChange`. If a customer has exactly one vehicle, it auto-selects.
 */
export function CustomerVehiclePicker({ value, onChange, testid = "cv-picker" }) {
    const [openC, setOpenC] = useState(false);
    const [searchC, setSearchC] = useState("");
    const [results, setResults] = useState([]);
    const [loading, setLoading] = useState(false);
    const [vehicles, setVehicles] = useState([]);
    const [loadingV, setLoadingV] = useState(false);
    const debounceRef = useRef(null);

    const picked = value || { customer: null, vehicle: null };

    // Search customers
    useEffect(() => {
        if (!openC) return;
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(async () => {
            setLoading(true);
            try {
                const params = { page: 1, page_size: 15 };
                if (searchC.trim()) params.q = searchC.trim();
                const r = await CustomerAPI.list(params);
                setResults(r.items || []);
            } finally { setLoading(false); }
        }, 220);
        return () => debounceRef.current && clearTimeout(debounceRef.current);
    }, [searchC, openC]);

    // When customer changes, load their vehicles
    useEffect(() => {
        if (!picked.customer) { setVehicles([]); return; }
        setLoadingV(true);
        VehicleAPI.list({ customer_id: picked.customer.id, page_size: 50 })
            .then((r) => {
                const items = r.items || [];
                setVehicles(items);
                // Auto-pick if exactly one vehicle and none picked yet
                if (items.length === 1 && !picked.vehicle) {
                    onChange && onChange({ customer: picked.customer, vehicle: items[0] });
                }
            })
            .finally(() => setLoadingV(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [picked.customer?.id]);

    function pickCustomer(c) {
        onChange && onChange({ customer: c, vehicle: null });
        setOpenC(false);
    }

    function pickVehicle(v) {
        onChange && onChange({ customer: picked.customer, vehicle: v });
    }

    return (
        <div className="space-y-3" data-testid={testid}>
            {/* Customer dropdown */}
            <Popover open={openC} onOpenChange={setOpenC}>
                <PopoverTrigger asChild>
                    <Button type="button" variant="outline" role="combobox" className="w-full justify-between font-normal" data-testid={`${testid}-customer-btn`}>
                        <span className="flex items-center gap-2 truncate">
                            <User className="h-4 w-4 shrink-0 text-zinc-400" />
                            {picked.customer ? (
                                <span className="truncate">
                                    {picked.customer.display_name}
                                    <span className="ml-1 font-mono text-xs text-zinc-500">· {picked.customer.phone}</span>
                                </span>
                            ) : (
                                <span className="text-zinc-400">Search customer…</span>
                            )}
                        </span>
                        <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                    <Command shouldFilter={false}>
                        <div className="flex items-center border-b px-3">
                            <Search className="mr-2 h-4 w-4 shrink-0 opacity-50" />
                            <CommandInput value={searchC} onValueChange={setSearchC} placeholder="Name, phone, code…" data-testid={`${testid}-customer-search`}
                                className="flex h-10 w-full rounded-md bg-transparent py-3 text-sm outline-none placeholder:text-muted-foreground" />
                            {loading && <Loader2 className="ml-2 h-4 w-4 animate-spin text-zinc-400" />}
                        </div>
                        <CommandList>
                            <CommandEmpty>
                                <div className="px-3 py-6 text-center text-sm text-zinc-500">
                                    {loading ? "Searching…" : "No customer match."}
                                </div>
                            </CommandEmpty>
                            <CommandGroup>
                                {results.map((c) => (
                                    <CommandItem key={c.id} value={c.id} onSelect={() => pickCustomer(c)}
                                        data-testid={`${testid}-customer-opt-${c.id}`}
                                        className="flex cursor-pointer items-center gap-2">
                                        <Check className={cn("h-4 w-4", picked.customer?.id === c.id ? "opacity-100" : "opacity-0")} />
                                        <div className="min-w-0 flex-1">
                                            <div className="truncate text-sm font-medium">{c.display_name}</div>
                                            <div className="text-xs text-zinc-500 font-mono">{c.phone} · {c.customer_code}</div>
                                        </div>
                                        {c.vehicle_count > 0 && (
                                            <span className="rounded bg-zinc-100 px-1.5 py-0.5 text-[10px] font-mono text-zinc-600 dark:bg-zinc-800 dark:text-zinc-400">
                                                {c.vehicle_count} veh
                                            </span>
                                        )}
                                    </CommandItem>
                                ))}
                            </CommandGroup>
                        </CommandList>
                    </Command>
                </PopoverContent>
            </Popover>

            {/* Vehicle list (chips) */}
            {picked.customer && (
                <div className="space-y-1.5">
                    <div className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500">Vehicle</div>
                    {loadingV ? (
                        <div className="flex items-center gap-2 text-sm text-zinc-500"><Loader2 className="h-3 w-3 animate-spin" /> loading vehicles…</div>
                    ) : vehicles.length === 0 ? (
                        <div className="text-sm text-zinc-500">No vehicles registered for this customer.</div>
                    ) : (
                        <div className="flex flex-wrap gap-1.5">
                            {vehicles.map((v) => (
                                <button type="button" key={v.id} onClick={() => pickVehicle(v)}
                                    data-testid={`${testid}-vehicle-${v.id}`}
                                    className={cn(
                                        "flex items-center gap-1.5 rounded-md border px-2.5 py-1.5 text-xs transition-colors",
                                        picked.vehicle?.id === v.id
                                            ? "border-brand-red bg-brand-redSoft text-brand-redDeep dark:border-brand-red dark:bg-brand-red/15 dark:text-brand-red"
                                            : "border-zinc-200 hover:border-zinc-300 dark:border-zinc-800 dark:hover:border-zinc-700"
                                    )}>
                                    <Car className="h-3.5 w-3.5" />
                                    <span className="font-mono">{v.registration_number}</span>
                                    <span className="text-zinc-500">· {v.make} {v.model}</span>
                                </button>
                            ))}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
